import { stringify as stringifyYaml } from "yaml";
import type { ComponentIR, JSONSchemaRef, OperationIR } from "../ir.js";
import type { GeneratedFile, GenerateOptions, Generator } from "./types.js";

function unwrapSchema(ref: JSONSchemaRef | undefined): Record<string, unknown> | undefined {
  if (!ref) {
    return undefined;
  }
  if ("schema" in ref && ref.schema && typeof ref.schema === "object") {
    return ref.schema as Record<string, unknown>;
  }
  if ("$ref" in ref) {
    return { $ref: ref.$ref };
  }
  return ref as Record<string, unknown>;
}

function buildEffects(
  artifactSlots: OperationIR["artifactSlots"],
): { reads?: string[]; writes?: string[] } | undefined {
  const reads: string[] = [];
  const writes: string[] = [];

  for (const [slotName, slot] of Object.entries(artifactSlots)) {
    if (slot.direction === "read" || slot.direction === "readwrite") {
      reads.push(slotName);
    }
    if (slot.direction === "write" || slot.direction === "readwrite") {
      writes.push(slotName);
    }
  }

  if (reads.length === 0 && writes.length === 0) {
    return undefined;
  }

  const effects: { reads?: string[]; writes?: string[] } = {};
  if (reads.length > 0) {
    effects.reads = reads;
  }
  if (writes.length > 0) {
    effects.writes = writes;
  }
  return effects;
}

function buildExits(op: OperationIR): Record<string, Record<string, unknown>> {
  const exits: Record<string, Record<string, unknown>> = {
    "0": {
      description: "Success",
    },
  };

  const successSchema = unwrapSchema(op.output.success);
  if (successSchema) {
    exits["0"].stdout = {
      format: "json",
      schema: successSchema,
    };
  }

  const failedSchema = unwrapSchema(op.output.failed);
  if (failedSchema) {
    const exitCode = op.errorModel.failureClasses.length > 0 ? "1" : "1";
    exits[exitCode] = {
      description: "Operation failed",
      stderr: {
        format: "json",
        schema: failedSchema,
      },
    };
  }

  for (let i = 0; i < op.errorModel.failureClasses.length; i++) {
    const code = String(i + 1);
    if (!exits[code]) {
      exits[code] = {
        description: `Failure: ${op.errorModel.failureClasses[i]}`,
      };
      if (failedSchema) {
        exits[code].stderr = {
          format: "json",
          schema: failedSchema,
        };
      }
    }
  }

  return exits;
}

function buildCommand(operationName: string, op: OperationIR): Record<string, unknown> {
  const command: Record<string, unknown> = {
    summary: op.description,
  };

  if (Object.keys(op.artifactSlots).length > 0) {
    const slots: Record<string, { direction: string }> = {};
    for (const [name, slot] of Object.entries(op.artifactSlots)) {
      slots[name] = { direction: slot.direction };
    }
    command.artifact_slots = slots;
  }

  if (op.options.length > 0) {
    command.options = op.options.map((opt) => {
      const entry: Record<string, unknown> = {
        name: opt.name,
        schema: opt.schema,
      };
      if (opt.description) {
        entry.description = opt.description;
      }
      if (opt.required) {
        entry.required = true;
      }
      return entry;
    });
  }

  const inputSchema = unwrapSchema(op.input);
  if (inputSchema) {
    command.streams = {
      stdin: { format: "json", schema: inputSchema },
    };
  }

  command.exits = buildExits(op);

  const effects = buildEffects(op.artifactSlots);
  if (effects) {
    command.effects = effects;
  }

  const xAgent: Record<string, unknown> = {
    risk_level: op.riskLevel,
    requires_confirmation: op.requiresConfirmation,
    idempotent: op.idempotent,
  };
  if (op.agentWorkflow) {
    xAgent.dsl_workflow = op.agentWorkflow;
  }
  if (op.agentTask) {
    xAgent.dsl_task = op.agentTask;
  }
  if (op.execution.timeoutMs !== undefined) {
    xAgent.expected_duration_ms = op.execution.timeoutMs;
  }
  command["x-agent"] = xAgent;

  return command;
}

const HOOK_CONTEXT_SCHEMA = {
  type: "object",
  properties: {
    command: { type: "string" },
    tool_name: { type: "string" },
    tool_input: {
      type: "object",
      properties: {
        file_path: { type: "string" },
        path: { type: "string" },
      },
    },
    file_path: { type: "string" },
    content: { type: "string" },
  },
};

const HOOK_RESPONSE_SCHEMA = {
  type: "object",
  properties: {
    permission: { type: "string", enum: ["deny"] },
    user_message: { type: "string" },
    agent_message: { type: "string" },
    additionalContext: { type: "string" },
  },
};

function buildGuardCommands(): Record<string, unknown> {
  return {
    "guard.evaluate": {
      summary:
        "Evaluate a guardrail hook (reads HookContext JSON from stdin, outputs HookResponse JSON to stdout)",
      streams: {
        stdin: { format: "json", schema: HOOK_CONTEXT_SCHEMA },
      },
      exits: {
        "0": {
          description: "Allowed — guardrail check passed",
          stdout: { format: "json", schema: HOOK_RESPONSE_SCHEMA },
        },
        "2": {
          description: "Denied — guardrail check blocked the request",
          stdout: { format: "json", schema: HOOK_RESPONSE_SCHEMA },
        },
      },
      "x-agent": {
        risk_level: "low",
        requires_confirmation: false,
        idempotent: true,
      },
    },
    "guard.list": {
      summary: "List active guardrails and enforcement policy",
      exits: {
        "0": {
          description: "Success",
          stdout: {
            format: "json",
            schema: {
              type: "object",
              properties: {
                policy: { type: "string" },
                entries: { type: "array" },
              },
            },
          },
        },
      },
      "x-agent": {
        risk_level: "low",
        requires_confirmation: false,
        idempotent: true,
      },
    },
    "guard.status": {
      summary: "Show recent guardrail evaluation summary",
      exits: {
        "0": {
          description: "Success",
          stdout: {
            format: "json",
            schema: {
              type: "object",
              properties: {
                hasHistory: { type: "boolean" },
                message: { type: "string" },
                recentEvents: { type: "array" },
              },
            },
          },
        },
      },
      "x-agent": {
        risk_level: "low",
        requires_confirmation: false,
        idempotent: true,
      },
    },
  };
}

function buildPromptCommand(): Record<string, unknown> {
  return {
    summary:
      "Send a natural language request — the right operation is selected automatically.",
    streams: {
      stdin: {
        format: "json",
        schema: {
          type: "object",
          required: ["message"],
          properties: {
            message: {
              type: "string",
              description:
                "Natural language request describing what you want to do",
            },
          },
        },
      },
    },
    options: [
      {
        name: "dry-run",
        schema: { type: "boolean" },
        description:
          "Show only the routing decision without executing the operation",
      },
    ],
    exits: {
      "0": {
        description: "Success",
        stdout: {
          format: "json",
          schema: {
            type: "object",
            properties: {
              selectedOperation: { type: "string" },
              mappedInput: { type: "object" },
              output: {},
              dryRun: { type: "boolean" },
              model: { type: "string" },
            },
          },
        },
      },
      "1": {
        description: "Routing or execution failed",
      },
    },
    "x-agent": {
      risk_level: "medium",
      requires_confirmation: false,
      idempotent: false,
      prompt_routing: true,
    },
  };
}

function countAgenticOperations(ir: ComponentIR): number {
  return Object.values(ir.operations).filter((op) => !op.handler).length;
}

/**
 * Build CLI commands contributed by external bindings.
 *
 * The core toolchain is binding-agnostic: for every declared binding it
 * exposes a uniform `<command>.sync` / `<command>.check` pair. `sync`
 * materializes the binding's output files; `check` is a read-only drift gate
 * (exit 2 on drift) for CI. The actual output logic lives in the binding
 * package's runtime function — the generated CLI only declares the contract.
 */
function buildBindingCommands(ir: ComponentIR): Record<string, unknown> {
  const commands: Record<string, unknown> = {};
  if (!ir.bindings) {
    return commands;
  }

  for (const [bindingId, binding] of Object.entries(ir.bindings)) {
    const cmd = binding.command;
    commands[`${cmd}.sync`] = {
      summary: `Materialize ${bindingId} binding output files from the embedded DSL`,
      options: [
        {
          name: "dry-run",
          schema: { type: "boolean" },
          description: "Report changes without writing files",
        },
      ],
      exits: {
        "0": { description: "Synced" },
        "1": { description: "Materialization failed" },
      },
      "x-agent": {
        risk_level: "low",
        requires_confirmation: false,
        idempotent: true,
        binding: bindingId,
      },
    };
    commands[`${cmd}.check`] = {
      summary: `Fail if ${bindingId} binding output drifts from the DSL`,
      exits: {
        "0": { description: "In sync" },
        "2": { description: "Drift detected" },
      },
      "x-agent": {
        risk_level: "low",
        requires_confirmation: false,
        idempotent: true,
        binding: bindingId,
      },
    };
  }

  return commands;
}

function buildCliContract(ir: ComponentIR): Record<string, unknown> {
  const commands: Record<string, unknown> = {};
  for (const [name, op] of Object.entries(ir.operations)) {
    commands[name] = buildCommand(name, op);
  }

  if (ir.governance?.guardrails === true) {
    Object.assign(commands, buildGuardCommands());
  }

  Object.assign(commands, buildBindingCommands(ir));

  if (countAgenticOperations(ir) >= 2) {
    commands.prompt = buildPromptCommand();
  }

  return {
    cli_contracts: "0.1.0",
    info: {
      title: ir.info.id,
      version: ir.info.version,
      description: ir.info.description ?? `${ir.info.id} component CLI`,
    },
    command_sets: {
      [ir.info.id]: {
        summary: ir.info.description ?? ir.info.id,
        global_options: [
          {
            name: "resume",
            schema: { type: "string" },
            description: "Resume from a previous memory_ref ID",
          },
          {
            name: "adapter",
            aliases: ["a"],
            schema: {
              type: "string",
              enum: ["mock", "claude", "gemini", "openai"],
            },
            description:
              "SDK adapter to use (overrides model_mapping default).",
          },
          {
            name: "model",
            schema: { type: "string" },
            description: "Model name override for the selected adapter.",
          },
          {
            name: "config",
            aliases: ["c"],
            schema: { type: "string" },
            description: "Path to project.yaml governance manifest.",
          },
        ],
        commands,
      },
    },
  };
}

const CLI_STUB_CONTENT = `extends: ./cli-contract.generated.yaml
`;

export const cliGenerator: Generator = {
  generate(ir: ComponentIR, _options: GenerateOptions): GeneratedFile[] {
    const contract = buildCliContract(ir);
    const yamlContent = `# Auto-generated by @aaac/contracts. Do not edit.\n${stringifyYaml(contract)}`;

    const files: GeneratedFile[] = [
      {
        path: "cli-contract.generated.yaml",
        content: yamlContent,
        overwrite: true,
      },
    ];

    if (ir.projections.includes("cli")) {
      files.push({
        path: "cli-contract.yaml",
        content: CLI_STUB_CONTENT,
        overwrite: false,
      });
    }

    return files;
  },
};
